/**
 * Cron helpers for the web schedule editor (`CronScheduleEditor`, which every
 * surface that edits a schedule renders). Pure logic only — no React.
 *
 * The platform-agnostic primitives (time formatting, `describeCron`,
 * `isValidCron`, `DAY_LABELS`) are shared with native in `@/shared/cronUtils`;
 * they're re-exported here so callers keep a single cron import. The
 * frequency-bucket model below is web-only (native uses a different
 * `CronSchedule` shape).
 */

import {
  buildHourList,
  type CronSchedule,
  parseCron as parseCronSchedule,
} from "@/shared/cronUtils.ts";

export {
  DAY_LABELS,
  describeCron,
  formatTime12,
  formatTimes12,
  isValidCron,
  type Period,
  to12Hour,
  to24Hour,
} from "@/shared/cronUtils.ts";

export type CronFrequency =
  | "every15m"
  | "every30m"
  | "hourly"
  | "every2h"
  | "every4h"
  | "daily"
  | "weekdays"
  | "weekly"
  | "monthly"
  | "custom";

export type RepeatCategory = "every" | "weekdays" | "weekly" | "monthly" | "custom";
export type EveryInterval = "every15m" | "every30m" | "hourly" | "every2h" | "every4h" | "daily";

export const everyIntervalLabels: Record<EveryInterval, string> = {
  every15m: "15 minutes",
  every30m: "30 minutes",
  hourly: "1 hour",
  every2h: "2 hours",
  every4h: "4 hours",
  daily: "day",
};

/** Repeat categories in menu order, with the copy each one reads as. */
export const repeatCategoryOptions: readonly { value: RepeatCategory; label: string }[] = [
  { value: "every", label: "Every" },
  { value: "weekdays", label: "Weekdays" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

/**
 * Narrow a select's payload back to the category it was built from.
 *
 * The underlying select is typed on `string`, so a guard is what carries the
 * option list's type across that boundary — an assertion there would just tell
 * the compiler to stop checking the one place a typo could land.
 */
export const isRepeatCategory = (value: string): value is RepeatCategory =>
  repeatCategoryOptions.some((option) => option.value === value);

const EVERY_INTERVALS: readonly EveryInterval[] = [
  "every15m",
  "every30m",
  "hourly",
  "every2h",
  "every4h",
  "daily",
];

/** The "Every" sub-intervals in menu order, paired with their labels. */
export const everyIntervalOptions: readonly { value: EveryInterval; label: string }[] =
  EVERY_INTERVALS.map((value) => ({ value, label: everyIntervalLabels[value] }));

/** The interval counterpart to {@link isRepeatCategory}. */
export const isEveryInterval = (value: string): value is EveryInterval =>
  EVERY_INTERVALS.some((interval) => interval === value);

export const frequencyToCategory = (f: CronFrequency): RepeatCategory => {
  switch (f) {
    case "every15m":
    case "every30m":
    case "hourly":
    case "every2h":
    case "every4h":
    case "daily":
      return "every";
    case "weekdays":
      return "weekdays";
    case "weekly":
      return "weekly";
    case "monthly":
      return "monthly";
    case "custom":
      return "custom";
  }
};

/**
 * Inverse of {@link frequencyToCategory}: resolve a repeat category back to a
 * concrete frequency. The "every" category needs the current interval to know
 * which sub-frequency (every15m, hourly, …) it maps to.
 */
export const categoryToFrequency = (
  category: Exclude<RepeatCategory, "custom">,
  interval: EveryInterval,
): Exclude<CronFrequency, "custom"> => {
  switch (category) {
    case "every":
      return interval;
    case "weekdays":
      return "weekdays";
    case "weekly":
      return "weekly";
    case "monthly":
      return "monthly";
  }
};

export const frequencyToInterval = (f: CronFrequency): EveryInterval => {
  switch (f) {
    case "every15m":
    case "every30m":
    case "hourly":
    case "every2h":
    case "every4h":
      return f;
    default:
      return "daily";
  }
};

export const isIntervalFrequency = (f: CronFrequency): boolean =>
  f === "every15m" || f === "every30m" || f === "hourly" || f === "every2h" || f === "every4h";

/**
 * Map a structured {@link CronSchedule} onto web's frequency buckets, or null
 * when the schedule falls outside them — an "every N" interval web has no preset
 * for (e.g. every 3 days, every 5 minutes) — so the editor treats it as custom.
 */
const scheduleToFrequency = (schedule: CronSchedule): CronFrequency | null => {
  const { interval } = schedule;
  switch (schedule.repeat) {
    case "minutely":
      return interval === 15 ? "every15m" : interval === 30 ? "every30m" : null;
    case "hourly":
      return interval === 1
        ? "hourly"
        : interval === 2
          ? "every2h"
          : interval === 4
            ? "every4h"
            : null;
    case "daily":
      return interval === 1 ? "daily" : null;
    case "weekdays":
      return "weekdays";
    case "weekly":
      return "weekly";
    case "monthly":
      return interval === 1 ? "monthly" : null;
  }
};

/**
 * Parse a 5-field cron string into web's frequency-bucket schedule state, or
 * null if it doesn't map onto a supported bucket (the editor then treats it as
 * custom). The raw parse is delegated to the shared `CronSchedule` parser;
 * interval frequencies (every15m, hourly, …) have no time-of-day, so they carry
 * placeholder `hours: [9], minute: 0`.
 */
export const parseCron = (
  cron: string,
): {
  frequency: CronFrequency;
  hours: number[];
  minute: number;
  daysOfWeek: number[];
  dayOfMonth: number;
} | null => {
  const schedule = parseCronSchedule(cron);
  if (!schedule) return null;
  const frequency = scheduleToFrequency(schedule);
  if (!frequency) return null;
  const { hours, minute, daysOfWeek, dayOfMonth } = schedule;
  return { frequency, hours, minute, daysOfWeek, dayOfMonth };
};

/**
 * Inverse of {@link parseCron}: build a 5-field cron string from schedule
 * state. Weekly with no selected days falls back to Monday (`1`); a time-of-day
 * frequency with no hours falls back to 9 AM, since an empty hour field is not
 * a cron.
 */
export const buildCron = (
  frequency: Exclude<CronFrequency, "custom">,
  hours: number[],
  minute: number,
  daysOfWeek: number[],
  dayOfMonth: number,
): string => {
  const m = String(minute);
  const h = hours.length > 0 ? buildHourList(hours) : "9";
  switch (frequency) {
    case "every15m":
      return "*/15 * * * *";
    case "every30m":
      return "*/30 * * * *";
    case "hourly":
      return "0 * * * *";
    case "every2h":
      return "0 */2 * * *";
    case "every4h":
      return "0 */4 * * *";
    case "daily":
      return `${m} ${h} * * *`;
    case "weekdays":
      return `${m} ${h} * * 1-5`;
    case "weekly":
      return `${m} ${h} * * ${daysOfWeek.length > 0 ? daysOfWeek.sort().join(",") : "1"}`;
    case "monthly":
      return `${m} ${h} ${dayOfMonth} * *`;
  }
};
